import { createRef, Component } from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import tippy from 'tippy.js';
import { checkerboardBackground } from '@cld/with-checkerboard-background';
import { calculateCrop } from 'utils/cropUtils/cropMath';
import t from 'common/i18n';
import './CropWidget.scss';

// Size thresholds (%)
const HIDE_HORIZONTAL_HANDLES_THRESHOLD = 20;
const HIDE_VERTICAL_HANDLES_THRESHOLD = 17;
const HIDE_CORNER_HANDLES_THRESHOLD = 11;
const DIMENSIONS_HORIZONTAL_THRESHOLD = 13;
const DIMENSIONS_VERTICAL_THRESHOLD = 15;

const CROP_AREA_HEIGHT = 478;
const CROP_AREA_WIDTH = 767;
const CROP_AREA_RATIO = CROP_AREA_WIDTH / CROP_AREA_HEIGHT;

// Crop dimensions (px) indicator
const GDimensions = styled.div.attrs({ className: 'bg-window-c bg-window absolute o-70 br1 pv2 ph3 f6 nowrap' })((props) => {
  const cropIsSmall = props.crop.height < DIMENSIONS_HORIZONTAL_THRESHOLD || props.crop.width < DIMENSIONS_VERTICAL_THRESHOLD;
  const cropBottom = props.crop.y + props.crop.height;
  return {
    top:
      // if short (height) and narrow (width) and has place below - position below
      cropIsSmall && cropBottom < 90
        ? `${cropBottom + 10}%`
        : // if short (height) and narrow (width) and no place below - position above
          cropIsSmall && cropBottom > 90
          ? `${props.crop.y}%`
          : // else - position inside
            `${cropBottom || 100}%`,
    left: `${props.crop.x + props.crop.width / 2 || 50}%`,
    transform: 'translate(-50%, -35px)',
    pointerEvents: 'none',
  };
});

const ReactCropWithCheckerboard = styled(ReactCrop)`
  ${checkerboardBackground};
`;

class CropWidget extends Component {
  static defaultProps = {
    cropSource: {},
    options: {},
  };

  state = {
    hideHandlesWestEast: false,
    hideHandlesNorthSouth: false,
    hideHandlesCorners: false,
    style: {
      height: '100%', // fixes safari bug with portrait images pushed to the left
    },
  };

  componentWillUnmount() {
    if (document.body) {
      document.body.removeEventListener('mouseup', this.enableAllTips, true);
    }
  }

  onChange = (crop, pixelCrop) => {
    if (crop.width && crop.height) {
      this.props.setCrop({ ...crop, pixelCrop });
    }

    this.setState({
      hideHandlesWestEast: crop.height < HIDE_VERTICAL_HANDLES_THRESHOLD,
      hideHandlesNorthSouth: crop.width < HIDE_HORIZONTAL_HANDLES_THRESHOLD,
      hideHandlesCorners: crop.width < HIDE_CORNER_HANDLES_THRESHOLD || crop.height < HIDE_CORNER_HANDLES_THRESHOLD,
    });
  };

  onImageLoaded = (image) => {
    setTimeout(() => {
      // firefox needs more time because image size can be 0 even though loaded
      if (this.widgetRef.current) {
        const { componentRef } = this.widgetRef.current;
        const cropWidgetHeight = componentRef.parentElement.clientHeight;
        const imageRatio = image.width / image.height;
        // This is needed to make images that are higher than the widget
        // respect the max-height property, whithout breaking centering
        // of smaller images (prevent this - https://codepen.io/tsi/pen/rvxeNE)

        if (cropWidgetHeight) {
          // Crop already rendered
          if (image.clientHeight < cropWidgetHeight) {
            this.setState({ style: { height: 'auto' } });
          }
        } else if (imageRatio > CROP_AREA_RATIO || image.height < CROP_AREA_HEIGHT) {
          // Crop widget not rendered yet but image is not a big portrait image.
          this.setState({ style: { height: 'auto' } });
          //TODO - check logic here - is last else-if never reached???
          // eslint-disable-next-line  no-dupe-else-if
        } else if (imageRatio < 1 && cropWidgetHeight && image.height > cropWidgetHeight) {
          // force 100% in case portrait image with height taller than widget height
          this.setState({ style: { height: '100%' } });
        }

        //turn HTMLCollection into an array of elements
        this.handleList = Array.prototype.slice.call(componentRef.getElementsByClassName('ReactCrop__drag-handle'));

        if (document.body) {
          document.body.addEventListener('mouseup', this.enableAllTips, true);
          this.setTooltips();
        }
      }

      // Set initial crop.
      // Normally, we could do this earlier but fixed-ratio crop defaults can be
      // calculated only after we know image proportions.
      const cropCalc = calculateCrop(this.props.options, image);

      this.props.setDefaultCrop({ ...cropCalc });

      this.props.setCrop({
        ...cropCalc,
        dimensions: [image.naturalWidth, image.naturalHeight],
      });

      // IE 11 hack
      // Set image width explicitly
      image.style.width = `${image.width}px`;
      // POP-2608: Safari cropping fix
      this.setState((prevState) => ({ style: { ...prevState.style, width: image.width } }));
    }, 100);
  };

  setTooltips = () => {
    if (this.handleList) {
      this.handleList.forEach((handle) => {
        handle.setAttribute('data-tippy-content', handle.dataset.ord.length > 1 ? t('crop.corner_tooltip') : t('crop.handle_tooltip'));
        tippy(handle, {
          animateFill: false,
          animation: 'shift-toward',
          delay: [500, 100],
        });
        handle.addEventListener('mousedown', () => {
          const instance = handle._tippy;
          instance.hide();
          instance.disable();
        });
      });
    }
  };

  enableAllTips = () => {
    if (this.handleList) {
      this.handleList.forEach((handle) => {
        if (handle._tippy) {
          handle._tippy.enable();
        }
      });
    }
  };

  handleList = null;

  widgetRef = createRef();

  cropWidget;

  dimensions;

  render() {
    const { cropSource, crop, options } = this.props;
    const { hideHandlesWestEast, hideHandlesNorthSouth, hideHandlesCorners } = this.state;
    const src = cropSource.src || '';
    return src ? (
      <ReactCropWithCheckerboard
        {...this.state}
        outputPixelUnits
        crop={crop}
        src={src}
        minWidth={crop.minWidth || undefined}
        minHeight={crop.minHeight || undefined}
        onChange={this.onChange}
        onImageLoaded={this.onImageLoaded}
        onImageError={this.props.onError}
        ref={this.widgetRef}
        className={cx({ hideHandlesWestEast, hideHandlesNorthSouth, hideHandlesCorners })}
        keepSelection={window.innerWidth < CROP_AREA_WIDTH}
      >
        {options.croppingShowDimensions && crop.pixelCrop && (
          <GDimensions crop={crop}>{`${parseInt(crop.pixelCrop.width)} x ${parseInt(crop.pixelCrop.height)} px`}</GDimensions>
        )}
      </ReactCropWithCheckerboard>
    ) : null;
  }
}

export default CropWidget;
